import { FC, useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans, useTrans } from '@libs/i18n';
import { GearSix, Translate } from '@phosphor-icons/react';

import { LanguageTab } from './Tabs/LanguageTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = observer(({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`settings-tabpanel-${index}`}
    aria-labelledby={`settings-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
));

const a11yProps = (index: number) => ({
  id: `settings-tab-${index}`,
  'aria-controls': `settings-tabpanel-${index}`,
});

const tabsConfig = [
  {
    label: <Trans i18nKey="app.settings.tabs.language" />,
    icon: <Translate size={20} />,
    component: <LanguageTab />,
  },
];

const tabStyles = {
  '& .MuiTabs-flexContainer': {
    backgroundColor: '#E0E0E0',
    borderRadius: '100px',
    p: 1,
  },
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    minHeight: '40px',
    borderRadius: '100px',
    gap: 1,
    color: 'text.secondary',
    border: '1px solid transparent',
    '&:hover': { backgroundColor: 'action.hover' },
    '&.Mui-selected': {
      backgroundColor: 'background.paper',
      color: 'text.primary',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    '&.Mui-disabled': {
      color: 'text.disabled',
      '& .MuiSvgIcon-root': { color: 'text.disabled' },
    },
  },
};

export const Settings: FC = observer(() => {
  const { t } = useTrans();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: 'background.default',
        p: 2,
        gap: 2,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          borderRadius: '16px',
          backgroundColor: 'background.paper',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          sx={{
            py: { xs: 2, sm: 3, md: 2 },
            px: { xs: 3, sm: 4, md: 5 },
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '46px',
              height: '46px',
              backgroundColor: '#8D12FF',
              borderRadius: '12px',
            }}
          >
            <GearSix size={32} color="white" />
          </Box>
          <Box>
            <Typography
              component="h1"
              sx={{ fontWeight: 500, fontSize: '16px', mb: 1, color: 'text.primary' }}
            >
              <Trans i18nKey="app.settings.title" />
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1rem' }}>
              <Trans i18nKey="app.settings.description" />
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ width: 'auto', display: 'inline-flex' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label={t('app.settings.tabs.ariaLabel')}
          sx={tabStyles}
        >
          {tabsConfig.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </Box>

      <Paper
        elevation={1}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          backgroundColor: 'background.paper',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 3, sm: 4, md: 5 },
            pb: { xs: 3, sm: 4, md: 5 },
            overflow: 'auto',
          }}
        >
          {tabsConfig.map((tab, index) => (
            <TabPanel key={index} value={activeTab} index={index}>
              {tab.component}
            </TabPanel>
          ))}
        </Box>
      </Paper>
    </Box>
  );
});
